import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTheme } from '../../context/ThemeContext';
import { routes } from '../../config/links';
import NavDropdown from '../NavDropdown/NavDropdown';
import OniLogo from '../OniLogo/OniLogo';
import RequestDemoButton from '../RequestDemoButton/RequestDemoButton';
import { handleHomeClick } from '../../utils/scroll';
import styles from './Navbar.module.css';

const navLinks = [
  { to: routes.features, label: 'Features' },
  { to: routes.howItWorks, label: 'How It Works' },
  { to: routes.technology, label: 'Technology' },
];

function MoonIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79Z"
        fill="currentColor"
      />
    </svg>
  );
}

function SunIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle cx="12" cy="12" r="4.5" fill="currentColor" />
      <g stroke="currentColor" strokeWidth="2" strokeLinecap="round">
        <path d="M12 2v2.5M12 19.5V22M2 12h2.5M19.5 12H22M4.9 4.9l1.8 1.8M17.3 17.3l1.8 1.8M19.1 4.9l-1.8 1.8M6.7 17.3l-1.8 1.8" />
      </g>
    </svg>
  );
}

function Navbar() {
  const { theme, toggleTheme } = useTheme();
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [solutionsOpen, setSolutionsOpen] = useState(false);

  const isOnSolutions =
    location.pathname === routes.solutions || location.pathname === routes.solutionsConcertVenues;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = menuOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [menuOpen]);

  useEffect(() => {
    const desktop = window.matchMedia('(min-width: 961px)');
    const onChange = (event: MediaQueryListEvent) => {
      if (event.matches) setMenuOpen(false);
    };
    desktop.addEventListener('change', onChange);
    return () => desktop.removeEventListener('change', onChange);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname, location.hash]);

  useEffect(() => {
    if (menuOpen) {
      setSolutionsOpen(isOnSolutions);
    }
  }, [menuOpen, isOnSolutions]);

  const closeMenu = () => setMenuOpen(false);

  return (
    <>
      <header className={`${styles.header} ${scrolled ? styles.scrolled : ''}`}>
        <div className={`container ${styles.inner}`}>
          <Link to={routes.home} className={styles.logoLink} aria-label="ONI home" onClick={handleHomeClick}>
            <OniLogo variant="compact" />
          </Link>

          <nav className={styles.desktopNav} aria-label="Primary">
            <Link
              to={routes.home}
              className={styles.navLink}
              onClick={handleHomeClick}
            >
              Home
            </Link>
            <NavDropdown label="Solutions" />
            {navLinks.map((link) => (
              <Link key={link.to} to={link.to} className={styles.navLink}>
                {link.label}
              </Link>
            ))}
            <Link to={routes.about} className={styles.navLink}>
              About
            </Link>
          </nav>

          <div className={styles.actions}>
            <button
              type="button"
              className={styles.themeToggle}
              onClick={toggleTheme}
              aria-label={theme === 'light' ? 'Switch to dark mode' : 'Switch to light mode'}
            >
              {theme === 'light' ? <MoonIcon /> : <SunIcon />}
            </button>
            <div className={styles.desktopCtas}>
              <RequestDemoButton size="md" />
            </div>
            <button
              type="button"
              className={styles.menuButton}
              aria-expanded={menuOpen}
              aria-controls="mobile-menu"
              aria-label={menuOpen ? 'Close menu' : 'Open menu'}
              onClick={() => setMenuOpen((open) => !open)}
            >
              <span />
              <span />
              <span />
            </button>
          </div>
        </div>
      </header>

      <div
        id="mobile-menu"
        className={`${styles.mobileMenu} ${menuOpen ? styles.mobileMenuOpen : ''}`}
        aria-hidden={!menuOpen}
      >
        <nav className={styles.mobileNav} aria-label="Mobile">
          <Link
            to={routes.home}
            className={styles.mobileNavLink}
            onClick={(event) => {
              handleHomeClick(event);
              closeMenu();
            }}
          >
            Home
          </Link>
          <div className={styles.mobileSolutions}>
            <button
              type="button"
              className={styles.mobileSolutionsTrigger}
              aria-expanded={solutionsOpen}
              aria-controls="mobile-solutions-panel"
              onClick={() => setSolutionsOpen((open) => !open)}
            >
              Solutions
              <span
                className={`${styles.mobileSolutionsChevron} ${solutionsOpen ? styles.mobileSolutionsChevronOpen : ''}`}
                aria-hidden="true"
              />
            </button>
            <div
              id="mobile-solutions-panel"
              className={`${styles.mobileSolutionsPanel} ${solutionsOpen ? styles.mobileSolutionsPanelOpen : ''}`}
            >
              <div className={styles.mobileSolutionsPanelInner}>
                <Link to={routes.solutions} className={styles.mobileSubNavLink} onClick={closeMenu}>
                  Stadiums &amp; Arenas
                </Link>
                <Link to={routes.solutionsConcertVenues} className={styles.mobileSubNavLink} onClick={closeMenu}>
                  Concert Venues
                </Link>
              </div>
            </div>
          </div>
          {navLinks.map((link) => (
            <Link
              key={link.to}
              to={link.to}
              className={styles.mobileNavLink}
              onClick={closeMenu}
            >
              {link.label}
            </Link>
          ))}
          <Link to={routes.about} className={styles.mobileNavLink} onClick={closeMenu}>
            About
          </Link>
          <div className={styles.mobileDemoWrap}>
            <RequestDemoButton size="md" />
          </div>
        </nav>
      </div>
    </>
  );
}

export default Navbar;
